/**
 * The strategic station tabs (M4 plan I6, spec §15 items 2 and 4) as M3 `StationServicePanel`s, so they mount in
 * M3's services panel and, through the M2 bridge (`bridge/m2/stationTabs.ts`), in M2's station screen alike:
 *
 * - **Station** (`economy`): the Station panel for the docked site (its strategic id is the site id, I0). It
 *   shows the cached system view's station at once and refreshes from `stationView`, then follows system views.
 * - **Wire** (`wire`): the Reach Wire panel with the client's items, following new ones.
 *
 * UI events only.
 */
import type { StationSite } from '../../galaxy/layout/types'
import type { PanelContext, StationServicePanel } from '../../reach/ports'
import type { StationView, SystemEconomyView, WireItem } from '../../strategic/types'
import { logger } from '../../core/logger'
import { mountReact } from './mount'
import { StationPanel } from './StationPanel'
import { stationPanelModel } from './stationPanelModel'
import { WirePanel } from './WirePanel'
import type { WireFilterContext } from './wireModel'

/** What the tabs read from the strategic client (`StrategicClient` satisfies it). */
export interface PanelClient {
  readonly systemView: SystemEconomyView | null
  readonly wire: readonly WireItem[]
  stationView(stationId: string): Promise<StationView>
  onSystemView(fn: (view: SystemEconomyView) => void): () => void
  onWire(fn: (item: WireItem) => void): () => void
}

export interface StrategicPanelDeps {
  client: PanelClient
  /** Where the player is and what they carry (the Wire filters). */
  filterContext: () => WireFilterContext
  /** A Wire item was chosen. */
  onSelectSystem: (systemId: string) => void
}

/** Panel order among M3's tabs (Charts 30, Parts 40, Scanner 50, Home 90): after Parts, before Home. */
export const STATION_PANEL_ORDER = 50
export const WIRE_PANEL_ORDER = 60

function cachedStation(client: PanelClient, id: string): StationView | null {
  const v = client.systemView
  if (v === null) return null
  for (const s of v.stations) if (s.id === id) return s
  return null
}

export function createStationInfoPanel(deps: StrategicPanelDeps): StationServicePanel {
  return {
    id: 'economy',
    title: 'Station',
    order: STATION_PANEL_ORDER,
    available: (): boolean => true,
    mount(el: HTMLElement, ctx: PanelContext): () => void {
      const id = ctx.station.id
      const mount = mountReact(el, 'sui-mount')
      let view = cachedStation(deps.client, id)
      let alive = true
      const render = (): void => {
        if (alive) mount.render(<StationPanel model={view === null ? null : stationPanelModel(view)} loading={view === null} />)
      }
      render()
      deps.client
        .stationView(id)
        .then((v) => {
          view = v
          render()
        })
        .catch((err: unknown) => {
          logger.warn('Station panel: no station view', { id, err })
          render()
        })
      const off = deps.client.onSystemView((sv) => {
        for (const s of sv.stations) {
          if (s.id === id) {
            view = s
            render()
            return
          }
        }
      })
      return (): void => {
        alive = false
        off()
        mount.unmount()
      }
    },
  }
}

export function createWireStationPanel(deps: StrategicPanelDeps): StationServicePanel {
  return {
    id: 'wire',
    title: 'Wire',
    order: WIRE_PANEL_ORDER,
    available: (): boolean => true,
    mount(el: HTMLElement, ctx: PanelContext): () => void {
      const mount = mountReact(el, 'sui-mount')
      const render = (): void => {
        mount.render(
          <WirePanel
            items={deps.client.wire}
            context={deps.filterContext()}
            onSelect={(systemId) => {
              ctx.close()
              deps.onSelectSystem(systemId)
            }}
          />,
        )
        ctx.refresh()
      }
      render()
      const off = deps.client.onWire(render)
      return (): void => {
        off()
        mount.unmount()
      }
    },
  }
}

/** Both tabs, ready to register into `ctx.reach.services`. */
export function createStrategicPanels(deps: StrategicPanelDeps): StationServicePanel[] {
  return [createStationInfoPanel(deps), createWireStationPanel(deps)]
}

/** The site's strategic station id (the site id, I0's M3 site source). */
export function strategicStationId(site: Pick<StationSite, 'id'>): string {
  return site.id
}
